import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/SEOHead';

export default function PrivacyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy',
    description: 'Privacy policy for Fresno Electrical Services',
    url: `${siteUrl}/privacy`
  };

  return (
    <>
      <SEOHead
        title="Privacy Policy | Fresno Electrical Services"
        description="Privacy policy for Fresno Electrical Services. Learn how we collect, use, and protect your personal information."
        canonical="/privacy"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20 border-t-4 border-yellow-500">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/2b87b4b9e5e0b37f784b16c7823e7fe3.jpg')] opacity-5 bg-cover bg-center"></div>
          <div className="container mx-auto px-6 relative z-10">
            <h1 className="text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-xl text-gray-300">Last Updated: January 2025</p>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 py-16 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Electrician Solutions Fresno ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our electrical services in Fresno, California and surrounding areas.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By using our website or services, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Information We Collect</h2>
              
              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you request a quote, schedule a service, or contact us, we may collect:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Full name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Service address (street, city, state, zip code)</li>
                <li>Property type (residential or commercial)</li>
                <li>Service details and project description</li>
              </ul>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">Automatically Collected Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you visit our website, we automatically collect:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>IP address and browser type</li>
                <li>Device information and operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website or source</li>
                <li>Date and time of visit</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Provide, operate, and maintain our electrical services</li>
                <li>Process service requests and provide quotes</li>
                <li>Communicate with you about appointments, services, and updates</li>
                <li>Send emergency service notifications (24/7 availability)</li>
                <li>Improve our website and customer experience</li>
                <li>Comply with legal obligations and C-10 electrical contractor licensing requirements</li>
                <li>Prevent fraud and ensure service quality</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to track activity on our website and store certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Information Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong className="text-gray-900">Service Providers:</strong> With trusted third-party vendors who assist in operating our website or conducting our business (e.g., payment processors, scheduling tools)</li>
                <li><strong className="text-gray-900">Legal Requirements:</strong> When required by law, court order, or government regulation</li>
                <li><strong className="text-gray-900">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong className="text-gray-900">Safety and Protection:</strong> To protect the rights, property, or safety of Electrician Solutions Fresno, our customers, or others</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to or restrict processing of your information</li>
                <li>Opt-out of marketing communications</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, please contact us using the information below.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">California Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect, the right to delete your information, and the right to opt-out of the sale of your information (we do not sell personal information).
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="bg-gray-50 border-l-4 border-yellow-500 p-6 rounded">
                <p className="text-gray-900 font-semibold mb-2">Electrician Solutions Fresno</p>
                <p className="text-gray-700">Fresno, California</p>
                <p className="text-gray-700">Phone: <a href="tel:+15595551234" className="text-yellow-600 hover:text-yellow-700 no-underline">(559) 555-1234</a></p>
                <p className="text-gray-700">Email: <a href="mailto:info@electriciansolutionsfresno.com" className="text-yellow-600 hover:text-yellow-700 no-underline">info@electriciansolutionsfresno.com</a></p>
                <p className="text-gray-700 mt-2 text-sm">Licensed C-10 Electrical Contractor</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Jurisdiction</h2>
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy is governed by the laws of the State of California and the United States. Any disputes arising from this policy shall be resolved in the courts of Fresno County, California.
              </p>
            </section>

          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
